/**
 * Mock 인증 상태 (BE 구현 전 임시 우회)
 *
 * 회의록 기반:
 *   - SNS 4종 + 이메일 진입 → mock credential
 *   - 본인인증 X, 익명성 보장
 *   - 회원가입 6단계 진행 후 mock 로그인
 *
 * 실제 BE 연결 시 이 파일 통째 제거 + authStore 통합
 *
 * 사용 위치:
 *   - Login.tsx        : SNS / 이메일 로그인 분기
 *   - Register*.tsx    : 회원가입 wizard 데이터 저장
 *   - AuthGuard.tsx    : mock 인증 사용자 여부 체크
 */

import { OAuthProvider } from 'src/services/oauth';

const USER_KEY = 'pwm:mock:user';
const PENDING_KEY = 'pwm:mock:pendingRegistration';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MockGender = 'female' | 'male' | 'other';

export interface MockUser {
  /** 가입 경로 (SNS provider 또는 'email') */
  authMethod: OAuthProvider | 'email';
  /** SNS 가입 시 이메일 (SNS에서 받아온 값) */
  email: string | null;
  /** 이메일 회원가입 시 로그인 ID (이메일 형식) */
  loginId: string | null;
  /** 이메일 회원가입 시 비밀번호 (암호화 필요 - 실제 BE 연동 시) */
  pwd: string | null;
  /** 닉네임 (필수) */
  nickname: string;
  /** 성별 */
  gender: MockGender | null;
  /** 만 나이 */
  age: number | null;
  /** 연령대 (만 나이 기준 그룹) — '20s' | '30s' | '40s' | '50s' | '60+' */
  ageGroup: string | null;
  /** 키 (cm) */
  heightCm: number | null;
  /** 현재 체중 (kg) */
  currentWeightKg: number | null;
  /** 목표 체중 (kg) */
  goalWeightKg: number | null;
  /** 가입 시각 */
  createdAt: string;

  // === 약물 등록 (wizard 2~3단계에서 같이 입력. 처방 전이면 null) ===
  drugCode: string | null; // medSid 문자열화 또는 레거시 코드
  drugName: string | null;
  doseAmount: string | null;
  medSid: number | null; // 약물 마스터 ID (BE TB_MED)
  medDoseSid: number | null; // 약물 용량 ID (BE TB_MED_DOSE)
  startDate: string | null;
  /** 투여 요일 (0=일요일, 1=월요일, ..., 6=토요일) - 주 1회 고정 요일 */
  injectionDay: number | null;
  /** 투여 시간 (HH:mm 형식, 예: "09:00") - 알림용 */
  injectionTime: string | null;
  /** 투여 간격 (일 단위, 기본 7일) - 최소 3일 */
  injectionInterval: number | null;

  // === 약관 동의 (wizard 6단계) ===
  agreedService: boolean;
  agreedPrivacy: boolean;
  agreedMarketing: boolean;
  /**
   * 사용자가 명시적으로 동의한 "선택" 약관의 trmsSid 목록.
   * /v1/trms 응답의 esntlAgreYn=false 약관 중 토글 ON 한 것만 담긴다.
   * (필수 약관은 가입 완료 = 동의 간주이므로 여기에 담지 않음)
   */
  agreedOptionalTermSids: number[];
}

/** 회원가입 wizard 진행 중 임시 저장 (단계 사이 이탈 시 복구) */
export interface PendingRegistration {
  authMethod: OAuthProvider | 'email';
  email: string | null;
  /** 현재 진행 중인 step (0-indexed) */
  step: number;
  partial: Partial<MockUser>;
}

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

export function getMockUser(): MockUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MockUser;
  } catch {
    return null;
  }
}

export function saveMockUser(user: MockUser): void {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearMockUser(): void {
  sessionStorage.removeItem(USER_KEY);
}

/**
 * 기존 사용자 데이터 부분 업데이트 (프로필 완성용)
 */
export function updateMockUser(updates: Partial<MockUser>): void {
  const user = getMockUser();
  if (!user) return;
  const updated = { ...user, ...updates };
  saveMockUser(updated);
}

export function isMockAuthenticated(): boolean {
  return getMockUser() !== null;
}

// ---------------------------------------------------------------------------
// Pending Registration (wizard 중간 상태)
// ---------------------------------------------------------------------------

export function getPending(): PendingRegistration | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingRegistration;
  } catch {
    return null;
  }
}

export function startPending(
  authMethod: OAuthProvider | 'email',
  email: string | null,
): PendingRegistration {
  const next: PendingRegistration = {
    authMethod,
    email,
    step: 0,
    partial: {
      authMethod,
      email,
    },
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(next));
  return next;
}

export function updatePending(patch: Partial<PendingRegistration>): PendingRegistration {
  const cur = getPending() ?? startPending('email', null);
  const next: PendingRegistration = {
    ...cur,
    ...patch,
    partial: { ...cur.partial, ...(patch.partial ?? {}) },
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(next));
  return next;
}

export function clearPending(): void {
  sessionStorage.removeItem(PENDING_KEY);
}

/** wizard 완료 → MockUser로 승격 + pending 제거 */
/**
 * 회원가입 완료 및 연동 데이터 자동 생성
 *
 * 생성되는 데이터:
 * 1. 사용자 정보 저장
 * 2. 약물 처방 정보 (drugCode가 있으면)
 * 3. 초기 체중 기록 (currentWeightKg가 있으면)
 */
export function finalizePending(): MockUser | null {
  const pending = getPending();
  if (!pending || !pending.partial.nickname) return null;

  const user: MockUser = {
    authMethod: pending.authMethod,
    email: pending.email,
    loginId: pending.partial.loginId ?? null,
    pwd: pending.partial.pwd ?? null,
    nickname: pending.partial.nickname,
    gender: pending.partial.gender ?? null,
    age: pending.partial.age ?? null,
    ageGroup: pending.partial.ageGroup ?? null,
    heightCm: pending.partial.heightCm ?? null,
    currentWeightKg: pending.partial.currentWeightKg ?? null,
    goalWeightKg: pending.partial.goalWeightKg ?? null,
    drugCode: pending.partial.drugCode ?? null,
    drugName: pending.partial.drugName ?? null,
    doseAmount: pending.partial.doseAmount ?? null,
    medSid: pending.partial.medSid ?? null,
    medDoseSid: pending.partial.medDoseSid ?? null,
    startDate: pending.partial.startDate ?? null,
    injectionDay: pending.partial.injectionDay ?? null,
    injectionTime: pending.partial.injectionTime ?? null,
    injectionInterval: pending.partial.injectionInterval ?? null,
    agreedService: pending.partial.agreedService ?? false,
    agreedPrivacy: pending.partial.agreedPrivacy ?? false,
    agreedMarketing: pending.partial.agreedMarketing ?? false,
    agreedOptionalTermSids: pending.partial.agreedOptionalTermSids ?? [],
    createdAt: new Date().toISOString(),
  };

  saveMockUser(user);
  clearPending();

  // 회원가입 정보 기반으로 연동 데이터 자동 생성
  initializeUserData(user);

  return user;
}

/**
 * 회원가입 시 입력한 정보를 기반으로 연동 데이터 자동 생성
 *
 * 1. 약물 처방 정보 (home/mock/state.ts의 savePrescription)
 * 2. 초기 체중 기록 (향후 W3에서 구현 예정)
 * 3. 루틴 설정 (처방 정보 기반 자동 생성)
 */
function initializeUserData(user: MockUser): void {
  // 1. 약물 처방 정보 저장
  if (
    user.drugCode &&
    user.drugName &&
    user.doseAmount &&
    user.startDate &&
    typeof user.injectionDay === 'number'
  ) {
    // home/mock/state.ts의 savePrescription 함수를 사용
    // 순환 참조 방지를 위해 직접 sessionStorage에 저장
    const prescription = {
      drugCode: user.drugCode,
      drugName: user.drugName,
      doseAmount: user.doseAmount,
      startDate: user.startDate,
      injectionDay: user.injectionDay,
      injectionTime: user.injectionTime || undefined,
      injectionInterval: user.injectionInterval ?? undefined,
    };
    sessionStorage.setItem('pwm.prescription', JSON.stringify(prescription));
  }

  // 2. 초기 체중 기록 (W3에서 구현 예정 - 실제 기록 API 연동 시)
  // if (user.currentWeightKg) {
  //   const initialWeightLog = {
  //     date: new Date().toISOString().split('T')[0],
  //     weightKg: user.currentWeightKg,
  //   };
  //   // weightLog API 저장
  // }
}
